import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { fileContentQueryOptions } from "@/hooks/use-user-data-viewer";
import { DocumentFrame } from "./document-frame";

interface DocumentViewerProps {
  fileId: string | null;
  fallbackTitle: string | null;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR");
}

export function DocumentViewer({ fileId, fallbackTitle }: DocumentViewerProps) {
  const query = useQuery(fileContentQueryOptions(fileId));

  if (!fileId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <FileText className="h-8 w-8" />
        <p className="text-sm">왼쪽 트리에서 문서를 선택하세요.</p>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-7 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="p-6 text-sm text-destructive">
        문서를 불러오지 못했습니다.
      </div>
    );
  }

  const file = query.data;
  const displayTitle = file.title || fallbackTitle || "제목 없음";

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-semibold tracking-tight">{displayTitle}</h2>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          {typeof file.charCount === "number" && (
            <span>{file.charCount.toLocaleString()}자</span>
          )}
          <span>수정 {formatDateTime(file.updatedAt)}</span>
          <span>생성 {formatDateTime(file.createdAt)}</span>
        </div>
      </div>
      <DocumentFrame html={file.content} />
    </div>
  );
}
